import { AuthScreen } from '@euler/app/flows/auth/Auth';
import { AuthContext } from '@euler/app/flows/auth/context';
import { AlertScreen } from '@euler/app/flows/common/AlertScreen';
import { ConfirmScreen } from '@euler/app/flows/common/ConfirmScreen';
import { PromptScreen } from '@euler/app/flows/common/PromptScreen';
import { ErrorScreen } from '@euler/app/flows/error/Error';
import { NotFoundScreen } from '@euler/app/flows/error/NotFound';
import { MediaPreviewScreen } from '@euler/app/flows/media/MediaPreviewScreen';
import { AlertContextProvider } from '@euler/app/tv/components/Alert';
import { printScreenInfo } from '@euler/app/tv/functions/utils';
import { AboutScreen } from '@euler/app/tv/pages/About';
import { DebugPanel } from '@euler/app/tv/pages/DebugPanel';
import { TVHomeScreen } from '@euler/app/tv/pages/Home';
import { MemberDetailScreen } from '@euler/app/tv/pages/MemberDetail';
import { SettingsScreen } from '@euler/app/tv/pages/Settings';
import { StoreInfoScreen } from '@euler/app/tv/pages/StoreInfo';
import { TaskFlow } from '@euler/app/tv/pages/TaskFlow';
import { UpdateScreen } from '@euler/app/tv/pages/Update';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { createStackNavigator } from '@react-navigation/stack';
import { StatusBar } from 'expo-status-bar';
import { memo, useContext } from 'react';
import { Platform, useWindowDimensions } from 'react-native';

const MainStack = createStackNavigator();

const ModalScreens = {
  _mediaPreview: MediaPreviewScreen,
  _error: ErrorScreen,
  _memberDetail: MemberDetailScreen,
  _debugPanel: DebugPanel,
  _update: UpdateScreen,
  _prompt: PromptScreen,
  _alert: AlertScreen,
  _confirm: ConfirmScreen,
};

export const TVScreens = {
  Home: TVHomeScreen,
  NotFound: NotFoundScreen,
  Task: TaskFlow,
  Settings: SettingsScreen,
  StoreInfo: StoreInfoScreen,
  About: AboutScreen,
  ...ModalScreens,
};

export type TVNavParams = ScreenToParams<typeof TVScreens>;

export const Routes = memo(() => {
  __DEV__ && printScreenInfo();

  const authContext = useContext(AuthContext);
  const theme = usePresentationTheme();
  const [{ isAuthenticated }] = useBehaviorSubject(authContext.authState$);
  const { height } = useWindowDimensions();

  if (isAuthenticated === undefined) return null;

  return (
    <AlertContextProvider>
      <StatusBar hidden />
      <MainStack.Navigator
        screenOptions={{
          headerBackTitleVisible: false,
          headerShown: false,
          cardStyle: {
            backgroundColor: theme.page.bg,
            height: Platform.OS === 'web' ? height : undefined,
          },
        }}
      >
        {isAuthenticated ? (
          <MainStack.Group screenOptions={{ headerShown: true }}>
            {spreadScreens(MainStack.Screen, TVScreens)}
          </MainStack.Group>
        ) : (
          <MainStack.Group screenOptions={{ headerShown: false }}>
            <MainStack.Screen name="Auth" component={AuthScreen} />
          </MainStack.Group>
        )}
      </MainStack.Navigator>
    </AlertContextProvider>
  );
});
